import React, { useState, useRef, useEffect } from 'react'
import { Formik, Form, FormikProps } from 'formik'
import { Button, Modal, message } from 'antd'
import { validateSchedule } from './scheduleValidation'
import ScheduleOptionsForm from './ScheduleOptionsForm'
import { ScheduleOptions, ScheduleOptionsForSubmission } from './scheduleTypes'
import Header from '../../CJHeader'
import axios from 'Utils/cj-axios'
import { useStoreState } from 'easy-peasy'

interface Props {
  value: {
    channel: string
    params?: any
    tableName?: string
    schedule?: string
    scheduleEnabled?: boolean
  }
  onClose: () => void
  onUpdateNode: (value: any) => void
  current: any
  setCurrent: React.Dispatch<React.SetStateAction<any>>
  setIsPopoverVisible: React.Dispatch<React.SetStateAction<boolean>>
  isVisible: boolean
  manualSyncEntranceNode: (customerJourneyId: string) => Promise<any>
  isRunning: boolean
}

const Entrance: React.FC<Props> = ({ 
  value, 
  onClose, 
  onUpdateNode, 
  current,
  setCurrent,
  setIsPopoverVisible,
  isVisible,
  manualSyncEntranceNode,
  isRunning
}) => {
  const { id } = useStoreState((state) => state.customerJourney)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [entranceList, setEntranceList] = useState<{ code: string; name: string; params?: any }[]>([])
  const formikRef = useRef<FormikProps<ScheduleOptions>>(null)

  const fetchEntrances = async () => {
    try {
      const resp = await axios.get('/customer-journeys/entrances')
      setEntranceList(resp.data)
    } catch (err) {
      console.error('Error fetching entrances:', err)
    }
  }

  useEffect(() => {
    if (isVisible) {
      fetchEntrances()
    }
  }, [isVisible])

  const initialFormValues: ScheduleOptions = {
    channel: value?.channel || '',
    params: value?.params || [],
    tableName: value?.tableName || '',
    schedule: value?.schedule || '',
    scheduleEnabled: value?.scheduleEnabled || false
  }

  const handleManualSync = async () => {
    if (!id) {
      message.error('Customer Journey ID is required for manual sync')
      return
    }

    if (!value?.tableName) {
      message.error('Table name is required for manual sync')
      return
    }

    try {
      setIsSubmitting(true)
      await manualSyncEntranceNode(id)
      message.success('Manual sync triggered successfully')
    } catch (error) {
      message.error('Failed to trigger manual sync')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleOk = () => {
    formikRef.current?.submitForm()
  }

  return (
    <Modal
      destroyOnClose
      maskClosable
      title={<Header 
        current={current} 
        setCurrent={setCurrent}
        setIsPopoverVisible={setIsPopoverVisible}
      />}
      width={700}
      closable={false}
      className="ChannelsModal"
      visible={isVisible}
      onCancel={() => {
        onClose()
        setIsPopoverVisible(false)
      }}
      footer={[
        <Button key="cancel" onClick={() => {
          onClose()
          setIsPopoverVisible(false)
        }}>
          Cancel
        </Button>,
        <Button
          key="manual"
          type="primary"
          onClick={handleManualSync}
          loading={isSubmitting}
          disabled={!id}
        >
          Manual Sync
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleOk}
          disabled={isRunning}
        >
          OK
        </Button>
      ]}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validate={(values) => {
          const errors: any = {}
          if (values.scheduleEnabled) {
            const scheduleErrors = validateSchedule(values)
            Object.assign(errors, scheduleErrors)
          }
          return errors
        }}
        onSubmit={async (values) => {
          try {
            setIsSubmitting(true)
            const updatedData: ScheduleOptionsForSubmission = {
              channel: values.channel || "All",
              params: values.params,
              tableName: values.scheduleEnabled ? values.tableName : undefined,
              schedule: values.scheduleEnabled ? values.schedule : undefined
            }

            onUpdateNode(updatedData)
            message.success('Configuration updated successfully')
            onClose()
          } catch (error) {
            message.error('Failed to update configuration')
          } finally {
            setIsSubmitting(false)
          }
        }}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form>
            <ScheduleOptionsForm
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              entrances={entranceList}
              disabled={isRunning}
            />
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default Entrance
